import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import * as E from '@react-email/components'
import {
	json,
	type LoaderFunctionArgs,
	redirect,
	type ActionFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Form, Link, useActionData, useSearchParams } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { requireAnonymous } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { prepareVerification } from './verify.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const SignupSchema = z.object({
	email: EmailSchema,
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()

	checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: SignupSchema.superRefine(async (data) => {
			const existingUser = await prisma.user.findUnique({
				where: { email: data.email },
				select: { id: true },
			})
			if (existingUser) {
				const url = new URL(request.url)
				const redirectTo = url.searchParams.get('redirectTo') || '/'
				// Redirect the user to the login page if the email already exists
				throw redirect(
					`/login?email=${encodeURIComponent(data.email)}&redirectTo=${encodeURIComponent(redirectTo)}`,
				)
			}
		}),
		async: true,
	})

	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { email } = submission.value
	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'onboarding',
		target: email,
	})

	const response = await sendEmail({
		to: email,
		subject: `Welcome to Reaction Videos!`,
		react: <SignupEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
	})

	if (response.status === 'success') {
		return redirect(redirectTo.toString())
	} else {
		return json(
			{
				result: submission.reply({ formErrors: [response.error.message] }),
			},
			{
				status: 500,
			},
		)
	}
}

export function SignupEmail({
	onboardingUrl,
	otp,
}: {
	onboardingUrl: string
	otp: string
}) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>Welcome to Reaction Videos!</E.Text>
				</h1>
				<p>
					<E.Text>
						Here's your verification code: <strong>{otp}</strong>
					</E.Text>
				</p>
				<p>
					<E.Text>Or click the link to get started:</E.Text>
				</p>
				<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
			</E.Container>
		</E.Html>
	)
}

export const meta: MetaFunction = () => {
	return [
		{ title: 'Sign up | Reaction Videos' },
		{
			name: 'description',
			content: `Sign up to share & vote on reaction videos`,
		},
		{
			name: 'og:title',
			content: `Sign up | Reaction Videos`,
		},
		{
			name: 'og:description',
			content: `Sign up to share & vote on reaction videos`,
		},
		// {
		// 	name: 'og:image',
		// 	content: 'https://reaction-videos.dev/og-image.png',
		// },
		{
			name: 'twitter:card',
			content: 'summary_large_image',
		},
		{
			name: 'twitter:title',
			content: `Sign up | Reaction Videos`,
		},
		{
			name: 'twitter:description',
			content: `Sign up to share & vote on reaction videos`,
		},
		// {
		// 	name: 'twitter:image',
		// 	content: 'https://reaction-videos.dev/og-image.png',
		// },
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	await requireAnonymous(request)
	return json({})
}

export default function SignupRoute() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'signup-form',
		constraint: getZodConstraint(SignupSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: SignupSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="mx-auto max-w-md">
			<Card>
				<CardHeader>
					<CardTitle>Let's start your journey!</CardTitle>
					<CardDescription>Please enter your email.</CardDescription>
				</CardHeader>
				<CardContent>
					<Form method="POST" {...getFormProps(form)}>
						<HoneypotInputs />
						<div className="flex flex-col gap-6">
							<Field
								labelProps={{
									htmlFor: fields.email.id,
									children: 'Email',
								}}
								inputProps={{
									...getInputProps(fields.email, { type: 'email' }),
									autoFocus: true,
									autoComplete: 'email',
								}}
								errors={fields.email.errors}
							/>
							<ErrorList errors={form.errors} id={form.errorId} />
							<StatusButton
								className="w-full"
								status={isPending ? 'pending' : (form.status ?? 'idle')}
								type="submit"
								disabled={isPending}
							>
								Submit
							</StatusButton>
						</div>
					</Form>
				</CardContent>
			</Card>
			<div className="mx-auto pt-4 text-center">
				<Button asChild variant="unstyled">
					<Link
						to={
							redirectTo ? `/login?${encodeURIComponent(redirectTo)}` : '/login'
						}
						className="font-bold"
					>
						Login
					</Link>
				</Button>
			</div>

			{/* <ul className="mt-5 flex flex-col gap-5 border-b-2 border-t-2 border-border py-3">
					{providerNames.map((providerName) => (
						<li key={providerName}>
							<ProviderConnectionForm
								type="Signup"
								providerName={providerName}
								redirectTo={redirectTo}
							/>
						</li>
					))}
				</ul> */}
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
